import { CustomerCareTemplate } from '../components/configurable';

export type CustomTileType = 'boolean' | 'number' | 'text';

export interface CustomTile {
  Added: string;
  Description: string;
  EndPoint: string;
  Id: string;
  Language: string;
  Modified: string;
  Name: string;
  SendAgentId: boolean;
  SendAgentRole: boolean;
  SendSubscriberExternalReferenceId: boolean;
  SendSubscriberId: boolean;
  Type: CustomTileType;
}

export enum CustomTemplateStatus {
  Active = 1,
  Inactive = 2
}

export enum IntegrationType {
  API = 2,
  URL = 1
}

/** Location for custom widget display */
export enum CustomWidgetLocation {
  Dashboard = 1,
  OrderFlow = 2
}

/** Order flow types for custom widget targeting (verification order type codes). */
export enum CustomWidgetOrderType {
  NewAdd = 1,
  EditOffer = 2,
  DisconnectRemove = 3,
  ModifyOrder = 4,
  ChangeOffer = 5
}

/**
 * Order flow steps for custom widget targeting.
 * Values align with {@link CodeTableVerificationOrderStep} / verification order step codes.
 */
export enum CustomWidgetOrderStep {
  Decisions = 1,
  Attributes = 2,
  CheckOut = 3
}

/**
 * Maps numeric CustomWidgetOrderStep values to their corresponding
 * string keys used for persisting order flow widget selections in the template.
 */
export const CustomWidgetOrderStepToKey: Record<CustomWidgetOrderStep, string> =
  {
    [CustomWidgetOrderStep.Decisions]: 'decisions',
    [CustomWidgetOrderStep.Attributes]: 'attributes',
    [CustomWidgetOrderStep.CheckOut]: 'checkout'
  };

export interface CustomWidget {
  Description: string;
  Expanded: boolean;
  Height: number;
  SendAgentId: boolean;
  SendAgentRole: boolean;
  SendSubscriberExternalReferenceId: boolean;
  SendSubscriberId: boolean;
  Status: CustomTemplateStatus;
  Title: string;
  URL: string;
  Added: string;
  Id: string;
  Modified: string;
  IntegrationTypeId: IntegrationType;
  /** 1=Dashboard, 2=OrderFlow */
  LocationId?: CustomWidgetLocation;
  /** Required when LocationId=OrderFlow. {@link CustomWidgetOrderType} code(s). */
  OrderTypeIds?: number[];
  /** Required when LocationId=OrderFlow. {@link CustomWidgetOrderStep} code. */
  OrderStepId?: number;
}

export interface CustomPage {
  Id: string;
  Title: string;
  Description?: string;
  IntegrationTypeId: IntegrationType;
  Status: CustomTemplateStatus;
  Added: string;
  Modified: string;
  URL?: string;
  SendAgentId?: boolean;
  SendAgentRole?: boolean;
  SendSubscriberExternalReferenceId?: boolean;
  SendSubscriberId?: boolean;
}

export interface GroupTemplate
  extends Pick<
    CustomerCareTemplate,
    'pages' | 'globalActions' | 'quickActions' | 'widgets' | 'subNavigation'
  > {
  Id: string;
  Name: string;
  GroupIds: number[];
  Added: string;
  Modified: string;
  landingPageWidgets: string[];
}

export interface CustomerCareTemplateConfiguration {
  Template?: CustomerCareTemplate;
  CustomWidgets?: CustomWidget[];
  CustomTiles?: CustomTile[];
  LandingPageWidgets?: CustomWidget[];
  CustomPages?: CustomPage[];
  GroupTemplates?: GroupTemplate[];
}

export type CustomerCareTemplateItem =
  | CustomerCareTemplate
  | CustomPage
  | CustomTile
  | CustomWidget
  | GroupTemplate;
